import colors from 'shared/colors';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useStatusProperty from '@/composables/goal/status-property';
import { AREA, DASH, DATETIME, DOT } from '@/lib/constants';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { formatTooltipCard, formatTooltipRow, syncReferenceLinesToAxis } from '@/composables/grid-page/chart/utils';
import { getColor } from '@/lib/charts/colors';
import { gridPageChartType } from 'shared/constants.json';
import { linearRegression } from '@/lib/charts/line-chart';
import { rgbaToHex } from 'shared/lib/color';
import { textByLang } from 'shared/lib/language';

const DATE_CATEGORY_FORMAT = 'LLL d, yyyy';

export default function useStatusDistributionChart(result, chart = ref({}), { showLegend = false } = {}) {
  const { userLang } = useLoggedInUser();
  const { statusProperty: goalStatusProperty } = useGoalProperty();
  const { leafOptions: statusOptions } = useStatusProperty(goalStatusProperty, userLang);

  const hasData = computed(() => result.value?.statusDistributionData?.length > 0);

  const startDate = computed(() => {
    if (!result.value?.startDate) {
      return DateTime.local();
    }
    return result.value.startDate;
  });

  const endDate = computed(() => {
    if (!result.value?.endDate) {
      return DateTime.local();
    }
    return result.value.endDate;
  });

  const startMs = computed(() => startDate.value?.toMillis() ?? 0);
  const endMs = computed(() => endDate.value?.toMillis() ?? 0);

  const isColumnMode = computed(() => chart.value.chartType === gridPageChartType.column);

  const seriesData = computed(() => {
    if (!hasData.value) {
      return [];
    }
    return statusOptions.value.map((opt) => ({
      uid: opt.uid,
      color: getColor(opt.color),
      name: textByLang(opt.label, userLang.value),
      data: result.value.statusDistributionData.map((point) => ({
        x: point.x,
        y: point.statusCounts[opt.uid] || 0,
      })),
    }));
  });

  const columnSeriesData = computed(() => {
    if (!hasData.value) {
      return [];
    }
    const sortedBuckets = result.value.statusDistributionData
      .slice()
      .sort((a, b) => a.x - b.x);
    return statusOptions.value.map((opt) => ({
      uid: opt.uid,
      color: getColor(opt.color),
      name: textByLang(opt.label, userLang.value),
      data: sortedBuckets.map((point) => ({
        x: point.x,
        y: point.statusCounts[opt.uid] || 0,
      })),
    }));
  });

  const calculateTrend = (seriesPoints) => {
    if (!startMs.value || !endMs.value) {
      return [];
    }
    const filtered = seriesPoints
      .filter((e) => e.x >= startMs.value && e.x <= endMs.value)
      .sort((a, b) => (a.x <= b.x ? -1 : 1));
    if (filtered.length === 0 || filtered[filtered.length - 1].x >= endMs.value) {
      return [];
    }
    const lastPoint = filtered[filtered.length - 1];
    const { slope, intercept } = linearRegression(filtered);
    const lastY = Math.max(0, intercept + (slope * endMs.value));
    return [
      { x: lastPoint.x + 1, y: lastPoint.y },
      { x: endMs.value, y: lastY },
    ];
  };

  const legendOptions = computed(() => (showLegend
    ? {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      y: 10,
      events: {
        itemClick(e) {
          const chart = this.chart;
          const visibleCount = chart.series.filter((s) => s.visible).length;
          if (visibleCount === 1 && e.legendItem.visible) {
            return false;
          }
          return true;
        },
      },
    }
    : { enabled: false }));

  const areaModeOptions = computed(() => ({
    chart: {
      animation: false,
      events: {
        render() {
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    legend: legendOptions.value,
    plotOptions: { area: { stacking: 'percent' } },
    series: [
      ...seriesData.value.map((c) => ({
        states: { inactive: { opacity: 1 } },
        type: AREA,
        lineColor: c.color,
        lineWidth: 0,
        data: calculateTrend(c.data),
        color: c.color,
        fillOpacity: 0.25,
        dashStyle: DASH,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      })),
      ...seriesData.value.map((c) => ({
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: c.data,
        color: c.color,
        fillOpacity: 0.25,
        name: c.name,
      })),
    ],
    time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    xAxis: {
      lineColor: rgbaToHex(colors.grey.lighten3),
      lineWidth: 1,
      type: DATETIME,
      minTickInterval: 28 * 24 * 3600 * 1000,
      minRange: 28 * 24 * 3600 * 1000,
      min: startMs.value,
      max: endMs.value,
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      gridLineColor: rgbaToHex(colors.grey.lighten3),
      gridLineDashStyle: DOT,
      title: { text: '' },
      labels: {
        formatter() {
          return `${this.value} %`;
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        const date = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${DateTime.fromMillis(this.x).toFormat(DATE_CATEGORY_FORMAT)}</div>`;
        const rows = this.points
          .filter((p) => p.y > 0)
          .map((p) => formatTooltipRow(p.color, p.series.name, p.y))
          .join('');
        return formatTooltipCard(date + rows);
      },
    },
  }));

  const columnModeOptions = computed(() => ({
    chart: {
      animation: false,
      type: 'column',
      events: {
        render() {
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    legend: legendOptions.value,
    plotOptions: {
      column: {
        stacking: 'percent',
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0.05,
      },
    },
    series: columnSeriesData.value.map((c) => ({
      states: { inactive: { opacity: 1 } },
      type: 'column',
      data: c.data,
      color: c.color,
      name: c.name,
    })),
    time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    xAxis: {
      lineColor: rgbaToHex(colors.grey.lighten3),
      lineWidth: 1,
      type: DATETIME,
      minTickInterval: 28 * 24 * 3600 * 1000,
      minRange: 28 * 24 * 3600 * 1000,
      min: startMs.value,
      max: endMs.value,
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      gridLineColor: rgbaToHex(colors.grey.lighten3),
      gridLineDashStyle: DOT,
      title: { text: '' },
      labels: {
        formatter() {
          return `${this.value} %`;
        },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        const date = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${DateTime.fromMillis(this.x).toFormat(DATE_CATEGORY_FORMAT)}</div>`;
        const rows = this.points
          .filter((p) => p.y > 0)
          .map((p) => formatTooltipRow(p.color, p.series.name, p.y))
          .join('');
        return formatTooltipCard(date + rows);
      },
    },
  }));

  const chartOptions = computed(() => (isColumnMode.value ? columnModeOptions.value : areaModeOptions.value));

  return {
    chartOptions,
    hasData,
  };
}
